import type { DocumentVersionRef } from "@readxx/contracts";
import { useState } from "react";

type ExpansionChoices = Readonly<Record<string, boolean>>;

export function directoryExpansionKey(ownerUserId: string | null, basis: DocumentVersionRef) {
  return `readxx:directory-expansion:${ownerUserId ?? "preview"}:${basis.documentId}:${basis.versionNumber}`;
}

function readChoices(key: string | undefined): ExpansionChoices {
  if (!key) return {};
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? "{}");
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(([, open]) => typeof open === "boolean"),
    );
  } catch {
    return {};
  }
}

/** Only manual choices persist; automatic ancestor reveal and search remain temporary. */
export function useDirectoryExpansion(key: string | undefined) {
  const [state, setState] = useState(() => ({ key, choices: readChoices(key) }));
  const choices = state.key === key ? state.choices : readChoices(key);
  const choose = (nodeId: string | Record<string, boolean>, open = true) => {
    const next = { ...choices, ...(typeof nodeId === "string" ? { [nodeId]: open } : nodeId) };
    setState({ key, choices: next });
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Directory interaction remains available when browser storage is unavailable.
    }
  };
  return [choices, choose] as const;
}
